import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { mockData } from '../utils/mockData';

const StatsSection = () => {
  const { language } = useLanguage();
  const content = mockData[language];
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const section = sectionRef.current;
    if (section) {
      observer.observe(section);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="sustainability" 
      data-testid="home-stats-section"
      className="py-24 bg-gradient-to-r from-teal-500 to-teal-600 text-white"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {content.stats.items.map((stat, index) => (
            <StatCounter 
              key={index} 
              stat={stat} 
              isVisible={isVisible}
              delay={index * 100}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const StatCounter = ({ stat, isVisible, delay, index }) => {
  const [count, setCount] = useState(() => parseInt(stat.number.replace(/[^0-9]/g, '')));
  const countRef = useRef(0);

  useEffect(() => {
    if (!isVisible) return;

    // Parse the number from stat.number (e.g., "50+" -> 50, "40M" -> 40)
    const targetValue = parseInt(stat.number.replace(/[^0-9]/g, ''));
    const duration = 2000; // 2 seconds
    const steps = 60;
    const increment = targetValue / steps;
    const stepDuration = duration / steps;

    let interval;
    const timer = setTimeout(() => {
      countRef.current = 0;
      interval = setInterval(() => {
        countRef.current += increment;
        if (countRef.current >= targetValue) {
          setCount(targetValue);
          clearInterval(interval);
        } else {
          setCount(Math.floor(countRef.current));
        }
      }, stepDuration);

    }, delay);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [isVisible, stat.number, delay]);

  // Format the display value with suffix
  const getDisplayValue = () => {
    if (stat.number.includes('M+')) {
      return `${count}M+`;
    } else if (stat.number.includes('M')) {
      return `${count}M`;
    } else if (stat.number.includes('+')) {
      return `${count}+`;
    } else if (stat.number.includes('-')) {
      return stat.number; // For "2-3" keep as is
    }
    return count;
  };

  return (
    <div className="text-center">
      <div data-testid={`home-stat-value-${index}`} className="text-5xl md:text-6xl font-bold mb-2 transition-all duration-300">
        {getDisplayValue()}
      </div>
      <div data-testid={`home-stat-label-${index}`} className="text-lg md:text-xl text-blue-100">
        {stat.label}
      </div>
      {stat.sublabel && (
        <div data-testid={`home-stat-detail-${index}`} className="text-sm text-blue-200 mt-1">
          {stat.sublabel}
        </div>
      )}
    </div>
  );
};

export default StatsSection;